// Px3 Pipeline - submit an order from the dashboard.
//
// The tester. It POSTs to the admin route, which hands the body to the SAME
// submitOrder() the portal-facing intake uses, with `source = null`. That
// sharing is the whole point: a tester that skipped a check would prove nothing
// about the real path, so a payload accepted here is a payload the portal can
// send, and a payload rejected here would have been rejected there.
//
// The textarea is prefilled from docs/px3-intake-contract.md, the document
// published to the people integrating against us, with one deliberate change:
// `dry_run` starts TRUE. This form buys domains' DNS, claims Google seats and
// pushes mailboxes to a customer's sequencer; the default for a paste-and-click
// box that does all that has to be the one that writes nothing.

import { useState } from 'react';

import type { Px3ShowToast } from '../Px3';
import {
  px3Api,
  px3ErrorMessage,
  px3PayloadIssues,
  type Px3PayloadIssue,
} from '../px3Api';

/** The contract's section 2 example, verbatim apart from `dry_run`. Placeholders
 *  in angle brackets are meant to be replaced - they will fail validation as
 *  they stand, which is the intended nudge. */
const EXAMPLE = `{
  "external_ref": "coldlabs-order-1234",
  "customer": "coldlabs-thinkvinculum",
  "callback": {
    "url": "https://pi-coldlabs.dev/api/provisioning/callback",
    "token": "opaque-secret-you-generate"
  },
  "registrar": { "provider": "dynadot", "api_key": "<Dynadot production API key>" },
  "dns": {
    "provider": "cloudflare",
    "account_id": "915cb88efecbe46d368bc9c3c75cd412",
    "api_token": "<Cloudflare token>"
  },
  "forwarding": { "target_url": "https://www.thinkvinculum.com/" },
  "users": [
    {
      "email": "drew@askthinkvinculum.com",
      "first_name": "Drew",
      "last_name": "Hart",
      "password": "Str0ng-Password!",
      "group": "Active",
      "photo_url": "https://cdn.example.com/drew.jpg"
    },
    {
      "email": "clara@askthinkvinculum.com",
      "first_name": "Clara",
      "last_name": "Vance",
      "password": "Str0ng-Password!",
      "group": "Backup",
      "photo_url": "https://cdn.example.com/clara.jpg"
    }
  ],
  "sequencer": {
    "provider": "instantly",
    "api_key": "<Instantly workspace API key>",
    "profile": "cold-labs",
    "settings": { "warmup": { "warmup_custom_ftag": "abc123" } }
  },
  "masterinbox": { "api_key": "pk_...", "workspace_id": 2464, "app_id": "google-6" },
  "options": { "dry_run": true }
}
`;

export function SubmitOrder({
  onOpenOrder,
  showToast,
}: {
  onOpenOrder: (orderId: string) => void;
  showToast: Px3ShowToast;
}): JSX.Element {
  const [text, setText] = useState(EXAMPLE);
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState<Px3PayloadIssue[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setIssues([]);
    setFailure(null);

    // Parsed here first so a stray comma reports as a JSON error with the
    // browser's own position information, instead of a 400 from a server that
    // only ever saw an unparseable body.
    let payload: unknown;
    try {
      payload = JSON.parse(text);
    } catch (err) {
      setFailure(`That is not valid JSON: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }

    setBusy(true);
    try {
      const result = await px3Api.submitOrder(payload);
      if (result.duplicate === true) {
        // A repeat of an (admin, external_ref) pair created NOTHING. Saying
        // "order accepted" would have the operator watching a build that is not
        // starting - it is the older order they are being shown.
        showToast(
          'warn',
          `That external_ref already exists - nothing new was created. Opening the existing order (${result.status}, stage ${result.stage}).`,
        );
      } else {
        showToast(
          'success',
          `Accepted: ${result.domains} domain(s), ${result.mailboxes} mailbox(es), ${result.users_per_domain} per domain.`,
        );
      }
      onOpenOrder(result.order_id);
    } catch (err) {
      const details = px3PayloadIssues(err);
      setIssues(details);
      setFailure(px3ErrorMessage(err));
      showToast('error', px3ErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="px3-card">
      <div className="px3-card-header">
        <h2 className="px3-card-title">Submit an order</h2>
        <div className="px3-actions">
          <button
            type="button"
            className="px3-btn px3-btn--ghost px3-btn--small"
            disabled={busy}
            onClick={() => {
              setText(EXAMPLE);
              setIssues([]);
              setFailure(null);
            }}
          >
            Reset to the example
          </button>
          <button
            type="button"
            className="px3-btn px3-btn--primary"
            disabled={busy || text.trim() === ''}
            onClick={() => void submit()}
          >
            {busy ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
      <div className="px3-card-body">
        <span className="px3-hint">
          The same gate as the portal intake, with the source recorded as admin. The example below
          is the one from <code>docs/px3-intake-contract.md</code> with <code>dry_run</code> set to
          true: a dry run validates the payload, runs every preflight check and computes the panel
          allocation, then stops without writing anything. Flip it to false only when you mean to
          build.
        </span>

        {failure !== null && (
          <div className="px3-notice px3-notice--err">
            <strong>Rejected:</strong> {failure}
          </div>
        )}

        {issues.length > 0 && (
          <div className="px3-notice px3-notice--err">
            {issues.map((issue, i) => (
              <div key={`${issue.path}-${i}`}>
                <code>{issue.path}</code> - {issue.message}
              </div>
            ))}
          </div>
        )}

        <div className="px3-field">
          <label className="px3-field-label" htmlFor="px3-submit-payload">
            Order payload (JSON)
          </label>
          <textarea
            id="px3-submit-payload"
            className="px3-textarea px3-textarea--tall"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
          <span className="px3-hint">
            Photos may ride inline as <code>photo_b64</code> (this surface parses up to 60 MB) or as
            an https <code>photo_url</code>. Every domain in the order must carry the same number of
            users, and every domain must already sit in the Dynadot account whose key is above.
          </span>
        </div>
      </div>
    </section>
  );
}
